import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  CogIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { useSupportedFormats } from "../../features/documents/hooks/useSupportedFormats";

export default function OrgStrategyPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [selectedStrategyId, setSelectedStrategyId] = useState("");
  // Document-type options come from the backend parser registry — the single
  // source of truth for supported formats.
  const { formatOptions: mimeOptions, isLoading: formatsLoading } =
    useSupportedFormats();
  const [message, setMessage] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create form state
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newDocTypes, setNewDocTypes] = useState<string[]>(["application/pdf"]);
  const [newParserType, setNewParserType] = useState("pdf");
  const [newExtractorType, setNewExtractorType] = useState(
    "anthropic-technical",
  );
  const [newChunkingType, setNewChunkingType] = useState("semantic");
  const [newFocusAreas, setNewFocusAreas] = useState<string[]>([]);
  const [newFocusAreaInput, setNewFocusAreaInput] = useState("");
  const [newCustomInstructions, setNewCustomInstructions] = useState("");

  // @ts-ignore - tRPC type issues
  const {
    data: strategies,
    isLoading: strategiesLoading,
    refetch: refetchStrategies,
  } = trpc.strategies.list.useQuery({});

  // @ts-ignore - tRPC type issues
  const {
    data: orgStrategyData,
    isLoading: orgLoading,
    refetch: refetchOrg,
  } = trpc.admin.getOrgStrategy.useQuery(
    { orgId: orgId! },
    { enabled: !!orgId },
  );

  // @ts-ignore - tRPC type issues
  const assignMutation = trpc.admin.assignStrategy.useMutation({
    onSuccess: () => {
      refetchOrg();
      setMessage("Strategy assigned successfully");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(`Error: ${err.message}`);
    },
  });

  // @ts-ignore - tRPC type issues
  const createMutation = trpc.strategies.create.useMutation({
    onSuccess: () => {
      refetchStrategies();
      setShowCreateForm(false);
      resetCreateForm();
      setMessage("Strategy created successfully");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(`Error: ${err.message}`);
    },
  });

  const resetCreateForm = () => {
    setNewName("");
    setNewDescription("");
    setNewDocTypes(["application/pdf"]);
    setNewParserType("pdf");
    setNewExtractorType("anthropic-technical");
    setNewChunkingType("semantic");
    setNewFocusAreas([]);
    setNewFocusAreaInput("");
    setNewCustomInstructions("");
  };

  const handleAssign = () => {
    if (!orgId || !selectedStrategyId) return;
    assignMutation.mutate({
      orgId,
      sourceStrategyId: selectedStrategyId,
    });
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      documentTypes: newDocTypes,
      config: {
        parserType: newParserType as "pdf" | "docx" | "xlsx" | "text",
        extractorType: newExtractorType,
        chunkingType: newChunkingType,
        extractorConfig: {
          ...(newFocusAreas.length > 0 && { focusAreas: newFocusAreas }),
          ...(newCustomInstructions && {
            customInstructions: newCustomInstructions,
          }),
        },
      },
    });
  };

  const handleAddFocusArea = () => {
    const trimmed = newFocusAreaInput.trim().toLowerCase().replace(/\s+/g, "_");
    if (trimmed && !newFocusAreas.includes(trimmed)) {
      setNewFocusAreas([...newFocusAreas, trimmed]);
      setNewFocusAreaInput("");
    }
  };

  const handleToggleDocType = (mime: string) => {
    setNewDocTypes((prev) =>
      prev.includes(mime) ? prev.filter((m) => m !== mime) : [...prev, mime],
    );
  };

  const isLoading = strategiesLoading || orgLoading;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  const activeStrategy = orgStrategyData?.activeStrategy;
  const systemStrategies = (strategies || []) as Array<{
    id: string;
    name: string;
    description: string | null;
    extractorType: string;
    chunkingType: string;
  }>;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/organizations"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Organizations
        </Link>
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          Strategy Management
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          View, create, and assign extraction strategies
        </p>
      </div>

      {/* Current Strategy */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CogIcon className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Current Strategy
          </h2>
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
              orgStrategyData?.isUsingCustom
                ? "bg-accent-tint text-accent"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {orgStrategyData?.isUsingCustom ? "Custom" : "System Default"}
          </span>
        </div>
        {activeStrategy ? (
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {activeStrategy.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {activeStrategy.description}
            </p>
            <div className="mt-2 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Parser: {activeStrategy.parserType}</span>
              <span>Extractor: {activeStrategy.extractorType}</span>
              <span>Chunking: {activeStrategy.chunkingType}</span>
              <span>Version: {activeStrategy.version}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Using system defaults — no custom strategy assigned
          </p>
        )}
      </div>

      {/* Assign Strategy */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Assign Strategy
        </h2>
        <div className="flex gap-3">
          <select
            value={selectedStrategyId}
            onChange={(e) => setSelectedStrategyId(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
          >
            <option value="">Select a strategy...</option>
            {systemStrategies.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.extractorType} / {s.chunkingType})
              </option>
            ))}
          </select>
          <button
            onClick={handleAssign}
            disabled={!selectedStrategyId || assignMutation.isLoading}
            className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 transition-colors"
          >
            {assignMutation.isLoading ? "Assigning..." : "Assign"}
          </button>
        </div>
      </div>

      {/* Create New Strategy */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-3">
            <PlusIcon className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Create New Strategy
            </h2>
          </div>
          {showCreateForm ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {showCreateForm && (
          <div className="mt-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Bioscience PDF Extractor"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Optimised for protocol manuals and SOPs"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Document Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Types
              </label>
              <div className="flex flex-wrap gap-3">
                {mimeOptions.length === 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatsLoading
                      ? "Loading supported formats…"
                      : "Supported formats unavailable — check the API health endpoint"}
                  </span>
                )}
                {mimeOptions.map((opt) => (
                  <label
                    key={opt.mime}
                    className="inline-flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={newDocTypes.includes(opt.mime)}
                      onChange={() => handleToggleDocType(opt.mime)}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Parser / Extractor / Chunking */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Parser Type
                </label>
                <select
                  value={newParserType}
                  onChange={(e) => setNewParserType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                >
                  <option value="pdf">PDF</option>
                  <option value="docx">DOCX</option>
                  <option value="xlsx">XLSX</option>
                  <option value="text">Text (Markdown / plain)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Extractor Type
                </label>
                <input
                  type="text"
                  value={newExtractorType}
                  onChange={(e) => setNewExtractorType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chunking Type
                </label>
                <select
                  value={newChunkingType}
                  onChange={(e) => setNewChunkingType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                >
                  <option value="semantic">Semantic (AI)</option>
                  <option value="fixed">Fixed Size</option>
                  <option value="smart">Smart (Regex)</option>
                </select>
              </div>
            </div>

            {/* Focus Areas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Focus Areas
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {newFocusAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-tint text-accent text-sm"
                  >
                    {area}
                    <button
                      onClick={() =>
                        setNewFocusAreas(
                          newFocusAreas.filter((a) => a !== area),
                        )
                      }
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFocusAreaInput}
                  onChange={(e) => setNewFocusAreaInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddFocusArea()}
                  placeholder="Add focus area"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleAddFocusArea}
                  className="px-3 py-2 bg-accent text-white rounded-lg text-sm hover:bg-accent-strong"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Custom Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Instructions
              </label>
              <textarea
                value={newCustomInstructions}
                onChange={(e) => setNewCustomInstructions(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Optional instructions appended to the AI prompt"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Create Button */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  resetCreateForm();
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-darkmode-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || createMutation.isLoading}
                className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 transition-colors"
              >
                {createMutation.isLoading ? "Creating..." : "Create Strategy"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <p
          className={`text-sm mt-4 ${message.startsWith("Error") ? "text-red-600" : "text-green-600"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
